import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../lib/auth/axiosApi';

// Post-game analysis: per-player error rate + a list of the moves that gave up
// the most equity, best play vs what was played. Heuristic (same 1-ply eval the
// bot uses) — good for spotting real mistakes, not a gnubg replacement.

const CLASS_LABEL = { blunder: 'blunder', error: 'error', inaccuracy: 'inaccuracy' };
const pct = (x) => `${(x * 100).toFixed(1)}%`;

export default function Analysis({ tableId, state, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    axiosInstance.get(`backgammon/${tableId}/analyze/`)
      .then((res) => setData(res.data))
      .catch(() => setErr('could not analyze this match'));
  }, [tableId]);

  const name = (pid) => ((state.participants || []).find((p) => p.pid === pid) || {}).name || '?';

  const moves = useMemo(() => {
    if (!data) return [];
    const list = data.moves || [];
    const shown = showAll ? list : list.filter((m) => m.class !== 'ok');
    return shown.slice().sort((a, b) => b.loss - a.loss);
  }, [data, showAll]);

  const playerRows = data ? Object.keys(data.players).map((pid) => ({ pid, ...data.players[pid] })) : [];

  return (
    <div className="bg-modal" onClick={onClose}>
      <div className="bg-modal-body bg-analysis" onClick={(e) => e.stopPropagation()}>
        <div className="bg-replay-head">
          <b>Match analysis</b>
          <span className="bg-hint">best play vs played · heuristic</span>
          <button className="bg-btn bg-btn-quiet" onClick={onClose}>close</button>
        </div>

        {err && <div className="bg-loading">{err}</div>}
        {!err && !data && <div className="bg-loading">crunching the match…</div>}
        {data && data.ready === false && <div className="bg-loading">no finished games to analyze yet.</div>}

        {data && data.ready !== false && (
          <>
            <div className="bg-an-players">
              {playerRows.map((p) => (
                <div key={p.pid} className="bg-an-card">
                  <div className="bg-an-name">{name(p.pid)}</div>
                  <div className="bg-an-rating">{p.rating}</div>
                  <div className="bg-an-stat"><span>avg equity lost / move</span><b>{pct(p.avg_loss)}</b></div>
                  <div className="bg-an-stat"><span>moves</span><b>{p.moves}</b></div>
                  <div className="bg-an-chips">
                    <span className="bg-an-chip bg-an-blunder">{p.blunder} blunders</span>
                    <span className="bg-an-chip bg-an-error">{p.error} errors</span>
                    <span className="bg-an-chip bg-an-inacc">{p.inaccuracy} inacc.</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-an-listhead">
              <span>{showAll ? 'All moves' : 'Mistakes'} ({moves.length})</span>
              <button className="bg-link" onClick={() => setShowAll((s) => !s)}>
                {showAll ? 'show mistakes only' : 'show all moves'}
              </button>
            </div>
            <div className="bg-an-list">
              {moves.length === 0 && <div className="bg-hint" style={{ padding: 10 }}>no mistakes flagged — clean play.</div>}
              {moves.map((m, i) => (
                <div key={`${m.game_no}-${m.n}-${i}`} className={`bg-an-row bg-an-row-${m.class}`}>
                  <span className="bg-an-row-meta">G{m.game_no} · {name(m.pid)} · {m.dice[0]}{m.dice[1]}</span>
                  <span className="bg-an-row-play">
                    played <b>{m.played}</b>
                    {m.class !== 'ok' && <> · best <b>{m.best}</b></>}
                  </span>
                  <span className={`bg-an-row-loss bg-an-${m.class}`}>
                    {m.class === 'ok' ? '✓' : `−${pct(m.loss)}${CLASS_LABEL[m.class] ? ` ${CLASS_LABEL[m.class]}` : ''}`}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
